import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/apis/api.service';
import {
    RegimeModel,
    RegimePeriodModel,
    ScheduleEntryModel,
    ScheduleMonthResponse,
    ShiftTemplateModel,
} from 'app/models/schedule.model';

export interface ScheduleUpsertInput {
    company_id: number;
    user_id: number;
    date: string;
    shift_id?: number | null;
    shift_template_id?: number | null;
    regime_id?: number | null;
    regime_slot?: number | null;
    regime_period_id?: number | null;
    status: string;
    obs?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ScheduleService {
    constructor(private readonly api: ApiService) {}

    // ----- Escala do mês -----
    getMonth(companyId: number, month: string) {
        return this.api.getSub<ScheduleMonthResponse>('schedule', { company_id: companyId, month });
    }

    generate(companyId: number, month: string, userIds?: number[]) {
        return this.api.postSub<{ affected: number }>('schedule/generate', {
            company_id: companyId,
            month,
            user_ids: userIds,
        });
    }

    upsertEntries(entries: ScheduleUpsertInput[]) {
        return this.api.postSub<{ affected: number }>('schedule/entries', { entries });
    }

    deleteEntry(scheduleEntryId: number) {
        return this.api.deleteSub(`schedule/entries/${scheduleEntryId}`);
    }

    // ----- Escala prevista dos médicos reguladores (cross-company) -----
    getRegulatorsDay(date: string) {
        return this.api.getSub<ScheduleEntryModel[]>('schedule/by-regulators/day', { date });
    }

    upsertRegulatorEntries(body: {
        user_id: number;
        company_id: number;
        date_start: string;
        date_end: string;
        start_time: string;
        end_time: string;
    }) {
        return this.api.postSub<{ affected: number }>('schedule/by-regulators/entries', body);
    }

    // ----- Regimes -----
    listRegimes(companyId: number) {
        return this.api.getSub<RegimeModel[]>('schedule/regimes', { company_id: companyId });
    }
    createRegime(body: Partial<RegimeModel>) {
        return this.api.postSub<RegimeModel>('schedule/regimes', body);
    }
    updateRegime(id: number, body: Partial<RegimeModel>) {
        return this.api.putSub<RegimeModel>(`schedule/regimes/${id}`, body);
    }
    updateRegimePeriods(
        regimeId: number,
        periods: {
            regime_period_id: number;
            name?: string | null;
            sigla?: string | null;
            color?: string | null;
            min_staff?: number | null;
        }[],
    ) {
        return this.api.putSub<RegimePeriodModel[]>(`schedule/regimes/${regimeId}/periods`, { periods });
    }
    deleteRegime(id: number) {
        return this.api.deleteSub(`schedule/regimes/${id}`);
    }

    // ----- Membros da equipe (elenco) -----
    listMembers(shiftId: number) {
        return this.api.getSub<any[]>(`schedule/shifts/${shiftId}/members`);
    }
    replaceMembers(shiftId: number, members: { user_id: number; role?: string }[]) {
        return this.api.putSub<any[]>(`schedule/shifts/${shiftId}/members`, { members });
    }

    // ----- Templates (catálogo de turnos) -----
    listTemplates(companyId: number) {
        return this.api.getSub<ShiftTemplateModel[]>('schedule/templates', { company_id: companyId });
    }
    createTemplate(body: Partial<ShiftTemplateModel>) {
        return this.api.postSub<ShiftTemplateModel>('schedule/templates', body);
    }
    updateTemplate(id: number, body: Partial<ShiftTemplateModel>) {
        return this.api.putSub<ShiftTemplateModel>(`schedule/templates/${id}`, body);
    }
    deleteTemplate(id: number) {
        return this.api.deleteSub(`schedule/templates/${id}`);
    }
}
